import { CommonModule } from '@angular/common';
import {
	Component,
	output,
	OnDestroy,
	ChangeDetectionStrategy,
	OnInit
} from '@angular/core';
import {
	FormBuilder,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import {
	PayTypes,
	PtoRequestService
} from '@app/pages/pto/pto-request.service';
import { SharedModule } from '@app/shared/shared.module';
import {Subject} from 'rxjs';

@Component({
	selector: 'app-pto-setting',
	imports: [CommonModule, FormsModule, ReactiveFormsModule, SharedModule],
	templateUrl: './pto-setting.component.html',
	styleUrl: './pto-setting.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class PtoSettingComponent implements OnInit, OnDestroy {
	payTypesForm!: FormGroup;
	payTypes: PayTypes[] = [];
	isLoading = false;
	isUpdating = false;
	isSavingSettings = false;
	duplicateError = false;

	formStatus = output<string>();

	private readonly destroy$ = new Subject<void>();

	hasInvalidEmails: boolean = false;
	hasDuplicateEmails: boolean = false;

	constructor(
		private readonly fb: FormBuilder,
		private readonly ptoService: PtoRequestService
	) {
		this.payTypesForm = this.fb.group({
			FMLARequestRecipients: [''],
			ReasonValidation: [true],
			ShiftImpact: [false]
		});
	}

	ngOnInit(): void {
		this.getAllPayType();
		this.loadPTOSettings();

		// Listen to form changes to check if it's touched or dirty
		this.payTypesForm.valueChanges.subscribe(() => {
			this.checkFormStatus();
		});
	}

	checkFormStatus() {
		const isFormDirty: any =
			this.payTypesForm.dirty || this.payTypesForm.touched;
		const hasErrors = this.hasEmailErrors();
		// Emit form status considering both dirty state and validation errors
		this.formStatus.emit(isFormDirty && !hasErrors);
	}

	getAllPayType(): void {
		this.isLoading = true;
		this.ptoService.getAllPayTypeAbsenceBySupervisor().subscribe({
			next: (data: PayTypes[]) => {
				this.payTypes = data;
				this.isLoading = false;
			},
			error: (error: any) => {
				this.isLoading = false;
			}
		});
	}

	// Load existing PTO settings (if you have a service method for this)
	loadPTOSettings(): void {
		this.ptoService.getPTOSettings().subscribe({
			next: (settings: any) => {
				// Use patchValue to update form without triggering auto-save
				this.payTypesForm.patchValue(
					{
						ReasonValidation: settings.ReasonValidation || false,
						ShiftImpact: settings.ShiftImpact || false,
						FMLARequestRecipients:
							settings.FMLARequestRecipients || ''
					},
					{ emitEvent: false }
				); // emitEvent: false prevents triggering valueChanges

				// Auto-resize textarea after loading data
				setTimeout(() => {
					this.resizeTextareaOnLoad();
					this.validateEmails();
				}, 0);
			},
			error: (error: any) => {
				// Set default values on error
				this.payTypesForm.patchValue(
					{
						ReasonValidation: true,
						ShiftImpact: false,
						FMLARequestRecipients: ''
					},
					{ emitEvent: false }
				);

				// Auto-resize textarea even for empty content
				setTimeout(() => {
					this.resizeTextareaOnLoad();
				}, 0);
			}
		});
	}

	onColorChange(index: number, event: Event): void {
		if (this.isUpdating) return; // Prevent multiple simultaneous updates

		const color = (event.target as HTMLInputElement).value;
		const payType = this.payTypes[index];

		// Update the local model
		payType.ColorCode = color;

		// Mark form as dirty to indicate changes
		this.payTypesForm.markAsDirty();
		this.checkFormStatus();

		// Save to API immediately
		this.savePayTypeColor(payType);
	}

	private savePayTypeColor(payType: PayTypes): void {
		this.isUpdating = true;

		const payload = {
			PayType: payType.PayType,
			ColorCode: payType.ColorCode
		};

		this.ptoService.savePayTypeColorMap(payload).subscribe({
			next: (response: any) => {
				this.isUpdating = false;
				// Reset form state after successful save
				this.payTypesForm.markAsPristine();
				this.checkFormStatus();
			},
			error: (error: any) => {
				this.isUpdating = false;
				// Mark form as dirty on error since changes weren't saved
				this.payTypesForm.markAsDirty();
				this.checkFormStatus();
			}
		});
	}

	onEmailInput(event: Event): void {
		const textarea = event.target as HTMLTextAreaElement;
		this.autoResize(textarea);
		this.validateEmails();
	}

	private autoResize(textarea: HTMLTextAreaElement): void {
		textarea.style.height = 'auto';
		const maxHeight = 120; // Maximum height in pixels
		const newHeight = Math.min(textarea.scrollHeight, maxHeight);
		textarea.style.height = newHeight + 'px';
		textarea.style.overflowY =
			textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
	}

	private resizeTextareaOnLoad(): void {
		const textarea = document.getElementById(
			'FMLARequestRecipients'
		) as HTMLTextAreaElement;
		if (textarea) {
			this.autoResize(textarea);
		}
	}

	private validateEmails(): void {
		const textareaValue =
			this.payTypesForm.get('FMLARequestRecipients')?.value || '';

		if (!textareaValue.trim()) {
			this.hasInvalidEmails = false;
			this.hasDuplicateEmails = false;
			this.checkFormStatus();
			return;
		}

		const emails = textareaValue
			.split(',')
			.map((email: string) => email.trim())
			.filter((email: string) => email.length > 0);

		// Check for invalid emails
		const invalidEmails = emails.filter(
			(email: string) => !this.isValidEmail(email)
		);
		this.hasInvalidEmails = invalidEmails.length > 0;

		// Check for duplicate emails (case-insensitive)
		const uniqueEmails = new Set(
			emails.map((email: string) => email.toLowerCase())
		);
		this.hasDuplicateEmails = uniqueEmails.size !== emails.length;

		// Update form status after validation
		this.checkFormStatus();
	}

	private isValidEmail(email: string): boolean {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	}

	getEmailErrorMessage(): string {
		if (this.hasInvalidEmails && this.hasDuplicateEmails) {
			return 'Please enter valid email addresses and remove duplicates.';
		} else if (this.hasInvalidEmails) {
			return 'Please enter valid email addresses separated by commas.';
		} else if (this.hasDuplicateEmails) {
			return 'Please remove duplicate email addresses.';
		}
		return '';
	}

	hasEmailErrors(): boolean {
		return this.hasInvalidEmails || this.hasDuplicateEmails;
	}

	savePTO() {
		if (
			this.payTypesForm.invalid ||
			this.isSavingSettings ||
			this.hasEmailErrors()
		) {
			return;
		}

		this.isSavingSettings = true;

		const payload = {
			ReasonValidation: this.payTypesForm.get('ReasonValidation')?.value,
			ShiftImpact: this.payTypesForm.get('ShiftImpact')?.value,
			FMLARequestRecipients: this.payTypesForm.get(
				'FMLARequestRecipients'
			)?.value
		};

		this.ptoService.savePTOSettings(payload).subscribe({
			next: (response: any) => {
				this.isSavingSettings = false;
			},
			error: (error: any) => {
				this.isSavingSettings = false;
			}
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
